import { useGetUserSettingsQuery } from '../../../api/combined'
import { useGetUnseenNewsCountQuery } from '../../../api/feed'
import { useNewsFeedLimitedAccessCheck } from '../../account/hooks/roleHooks'
import { useTrackedGames } from '../../live-events/hooks/useTrackedGames'
import { useCurrentMarket } from '../../markets/hooks/useCurrentMarket'
import { useFreeLiveEventsGamesSetting } from '../../settings'
import { NewsEntryType } from '../types/UnifiedNewsEntry'

/**
 * Hook to get the last seen news timestamp
 * @returns last seen news timestamp
 */
export const useLastSeenNewsTimestamp = () => {
  const { data: userSettings, isFetching, isLoading, isSuccess, error } = useGetUserSettingsQuery()
  const lastSeenNewsTimestamp = userSettings?.lastSeenNews

  return {
    data: lastSeenNewsTimestamp,
    isFetching: isFetching,
    isLoading: isLoading,
    isSuccess: isSuccess,
    error: error,
  }
}

/**
 * Hook to get the last seen news count
 * @returns last seen news count
 */
export const useLastSeenNewsCount = () => {
  const { currentMarketIso: marketIso } = useCurrentMarket()
  const types = Object.values(NewsEntryType)

  const {
    data: lastSeenNewsTimestamp,
    isFetching: isFetchingLastSeenNewsTimestamp,
    isLoading: isLoadingLastSeenNewsTimestamp,
    isSuccess: isSuccessLastSeenNewsTimestamp,
    error: lastSeenNewsTimestampError,
  } = useLastSeenNewsTimestamp()

  const {
    data: lastSeenNewsCount,
    isFetching: isFetchingLastSeenNewsCount,
    isLoading: isLoadingLastSeenNewsCount,
    isSuccess: isSuccessLastSeenNewsCount,
    error: lastSeenNewsCountError,
  } = useGetUnseenNewsCountQuery(
    {
      timestamp: lastSeenNewsTimestamp as number,
      marketIso: marketIso,
      types: types,
    },
    { skip: !lastSeenNewsTimestamp || !marketIso || !types }
  )

  return {
    data: lastSeenNewsCount as number | undefined,
    isLoading: isLoadingLastSeenNewsTimestamp || isLoadingLastSeenNewsCount,
    isFetching: isFetchingLastSeenNewsTimestamp || isFetchingLastSeenNewsCount,
    isSuccess: isSuccessLastSeenNewsTimestamp && isSuccessLastSeenNewsCount,
    error: lastSeenNewsTimestampError || lastSeenNewsCountError,
  }
}

/**
 * Hook to check if news content should be limited for a specific game
 * @param gameId - The game ID to check
 * @returns boolean indicating if content should be limited
 */
export const useNewsContentLimitedCheck = (gameId: string) => {
  const hasNewsFeedLimitedAccess = useNewsFeedLimitedAccessCheck()
  const freeLiveEventsGamesSetting = useFreeLiveEventsGamesSetting()
  const trackedGamesQuery = useTrackedGames({})

  if (hasNewsFeedLimitedAccess) return true

  const hasTrackedGamePermission = trackedGamesQuery.data?.some((tg) => tg.game.id === gameId && tg.permission)
  if (hasTrackedGamePermission) return false

  const freeLiveEventsGames = freeLiveEventsGamesSetting.data?.data?.free_live_events_games as string[] | undefined
  const isInFreeLiveEventsGames = freeLiveEventsGames && Array.isArray(freeLiveEventsGames) && freeLiveEventsGames.includes(gameId)
  if (isInFreeLiveEventsGames) return false

  return true
}
